import * as R from 'ramda';

import * as CheckoutActions from 'components/Checkout/redux/actions';
import store from 'redux/store';
import { fnIsEventAtCapacity } from 'components/Events/helpers';
import { truthyDadValues } from 'utils/common';
import { fnBuildRegisteredGuest } from './guestHelpers';
/**
 * Removes superfluous data from the event object
 * to reduce how much data goes into the Redux store's cart.
 *
 * @param {object} oEvent - Event details data from DAD.
 * @returns {object}
 */
const fnPrepEventForCart = (oEvent) =>
  R.omit(['ATTENDEES', 'MY_PARTY'], oEvent);

/**
 * Convert the MY_PARTY object to an array of guests.
 * @param {object} oEvent - Event details data from DAD.
 * @returns {array}
 */
export const fnConvertMyPartyToGuestsArray = (oEvent) => {
  const { HOST, GUESTS = [] } = oEvent?.MY_PARTY || {};
  let aMyPartyGuests = [];
  if (GUESTS.length > 0) {
    aMyPartyGuests = GUESTS.map(
      ({
        NAME,
        LOOKUPID,
        GUEST_MAX_PAST_COST,
        REGISTRANTID,
        REG_OPTION,
        ADDITIONAL_INFO,
      }) =>
        fnBuildRegisteredGuest(
          NAME,
          'guest',
          LOOKUPID,
          GUEST_MAX_PAST_COST,
          REGISTRANTID,
          REG_OPTION,
          ADDITIONAL_INFO
        )
    );
  }

  // The host might have paid for multiple sub events in one transaction,
  // so we need to calculate the total amount paid for this event.
  const nTotalAmountPaidForThisEvent = aMyPartyGuests.reduce(
    (nTotal, oGuest) => nTotal + (oGuest.GUEST_MAX_PAST_COST || 0),
    HOST?.GUEST_MAX_PAST_COST || 0
  );

  const oHost = fnBuildRegisteredGuest(
    HOST.NAME,
    'currentHost',
    HOST.LOOKUPID,
    HOST.GUEST_MAX_PAST_COST,
    HOST.REGISTRANTID,
    HOST.REG_OPTION,
    HOST.ADDITIONAL_INFO,
    nTotalAmountPaidForThisEvent
  );

  const aGuests = [oHost, ...aMyPartyGuests];
  return aGuests;
};

const fnGetEventCartItem = (sEventLookupId) =>
  store.getState().Checkout.aCart.find((oItem) => oItem.sId === sEventLookupId);

export const fnAddEventToCart = (oEvent) => {
  const oCartItem = {
    sType: 'event',
    sId: oEvent.LOOKUPID,
    sDisplayName: oEvent.TITLE,
    nPrice: 0,
    oDetails: {
      oEvent: fnPrepEventForCart(oEvent),
      aGuests: [],
    },
  };
  store.dispatch(CheckoutActions.cart.addItem(oCartItem));
};

export const fnAddEventRegistrationCancellationToCart = (oEvent) => {
  const oCartItem = {
    sType: 'event-cancellation',
    sId: oEvent.LOOKUPID,
    sDisplayName: oEvent.TITLE,
    nPrice: 0,
    oDetails: {
      oEvent: fnPrepEventForCart(oEvent),
      aGuests: fnConvertMyPartyToGuestsArray(oEvent),
    },
  };
  store.dispatch(CheckoutActions.cart.addItem(oCartItem));
};

export const fnRemoveEventFromCart = (sId) => {
  store.dispatch(CheckoutActions.cart.removeItem(sId));
};

export const fnAddEventRegistrationEditToCart = (oEvent) => {
  // Editing currently only supports one event at a time. Clear the cart.
  const aEventsInCart = store.getState().Checkout.aCart;
  aEventsInCart.forEach((oEventInCart) => {
    fnRemoveEventFromCart(oEventInCart.sId);
  });
  // Add the event to the cart.
  const oCartItem = {
    sType: 'event-edit',
    sId: oEvent.LOOKUPID,
    sDisplayName: oEvent.TITLE,
    nPrice: 0,
    oDetails: {
      oEvent: fnPrepEventForCart(oEvent),
      aGuests: fnConvertMyPartyToGuestsArray(oEvent),
    },
  };
  store.dispatch(CheckoutActions.cart.addItem(oCartItem));
};

export const fnAddWaitlistEventToCart = (oEvent) => {
  const oCartItem = {
    sType: 'event-waitlist',
    sId: oEvent.LOOKUPID,
    sDisplayName: oEvent.TITLE,
    nPrice: 0,
    oDetails: {
      oEvent: fnPrepEventForCart(oEvent),
      aGuests: [],
    },
  };
  store.dispatch(CheckoutActions.cart.addItem(oCartItem));
};

export const fnRefreshEventDataInCart = (oEvent) => {
  const oEventCartItem = fnGetEventCartItem(oEvent.LOOKUPID);
  const oEventCartItemCopy = R.clone(oEventCartItem);

  const oRefreshedEventCartItem = {
    ...oEventCartItemCopy,
    sDisplayName: oEvent.TITLE,
    oDetails: {
      ...oEventCartItemCopy.oDetails,
      oEvent: fnPrepEventForCart(oEvent),
    },
  };

  store.dispatch(CheckoutActions.cart.updateItem(oRefreshedEventCartItem));
};

export const fnUpdateEventCapacityInCart = (
  sEventLookupId,
  iNewCapacityRemaining
) => {
  const oEventCartItem = fnGetEventCartItem(sEventLookupId);
  const { TOTAL_REGISTRATIONS, TOTAL_CAPACITY } =
    oEventCartItem.oDetails.oEvent.REGISTRATION;

  const iOldCapacityRemaining = TOTAL_CAPACITY - TOTAL_REGISTRATIONS;
  if (TOTAL_CAPACITY > 0 && iOldCapacityRemaining !== iNewCapacityRemaining) {
    const oUpdatedEventCartItem = R.clone(oEventCartItem);
    oUpdatedEventCartItem.oDetails.oEvent.REGISTRATION.TOTAL_REGISTRATIONS =
      TOTAL_CAPACITY - iNewCapacityRemaining;
    store.dispatch(CheckoutActions.cart.updateItem(oUpdatedEventCartItem));
  }
};

/**
 * Changes the event item in the cart's registration type(sType) to 'event-waitlist'
 */
export const fnUpdateEventRegistrationTypeToWaitlist = (sEventLookupId) => {
  const oEventCartItem = fnGetEventCartItem(sEventLookupId);
  const oUpdatedEventCartItem = R.clone(oEventCartItem);
  oUpdatedEventCartItem.sType = 'event-waitlist';
  store.dispatch(CheckoutActions.cart.updateItem(oUpdatedEventCartItem));
};

/**
 * Adds/removes a sub event to/from the cart based on
 * the user checking or un-checking that sub event's checkbox.
 *
 * @param {object} oEvent - Event details data from DAD.
 * @param {boolean} bIsChecked - Checkbox state (true if checked, false if unchecked).
 */
export const fnToggleEventInCart = (oEvent, bIsChecked) => {
  const bIsAtCapacity = fnIsEventAtCapacity(oEvent);
  const bIsWaitlistEvent = truthyDadValues.includes(
    oEvent?.REGISTRATION?.ALLOW_WAITLIST
  );
  // @todo: handle edits to event registrations
  if (bIsChecked) {
    if (bIsAtCapacity && bIsWaitlistEvent) {
      fnAddWaitlistEventToCart(oEvent);
    } else if (!bIsAtCapacity) {
      fnAddEventToCart(oEvent);
    }
  } else {
    fnRemoveEventFromCart(oEvent.LOOKUPID);
  }
};
